import { isTruthyFlag } from "./UserUtils";
import type {
  BetApiRecord,
  BetSelection,
  BetStatusDisplay,
  BetTableRow,
} from "../types/bets";

const handleBetStatus = (
  bet: BetApiRecord | undefined,
  fallback: BetStatusDisplay = "Pending",
): BetStatusDisplay => {
  if (
    typeof bet?.display_status === "string" &&
    bet.display_status.trim() !== ""
  ) {
    return bet.display_status;
  }

  const statusMap: Record<string, BetStatusDisplay> = {
    "1": "Won",
    "0": "Lost",
  };
  return statusMap[String(bet?.won ?? "")] || fallback;
};

const handleSelection = (record: BetApiRecord | undefined): string => {
  const outcome = record?.selection?.market?.outcome?.[0];
  return outcome?.name ?? "-";
};

const handleMarket = (record: BetApiRecord | undefined): string => {
  return record?.selection?.market?.market_name ?? "-";
};

/** Coerce to a finite number for display; avoids .toFixed on NaN/null/undefined. */
const toSafeNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  const n = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const formatCombinedOdds = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toFixed(2) : "-";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!trimmedValue) return "-";

    // Signed American odds from the API (e.g. "-125", "+200") — keep as-is.
    // Unsigned integers are decimal odds (e.g. "3") and still get two decimals.
    if (/^[+-]\d+$/.test(trimmedValue)) {
      return trimmedValue;
    }

    const numericValue = Number(trimmedValue);
    if (
      !Number.isNaN(numericValue) &&
      Number.isFinite(numericValue) &&
      /^-?\d+(\.\d+)?$/.test(trimmedValue)
    ) {
      return numericValue.toFixed(2);
    }

    return trimmedValue;
  }

  return String(value);
};

type IsGroupBetFn = (bet: BetApiRecord) => boolean;

export const processBets = (
  bets: BetApiRecord[],
  isGroupBetCondition: IsGroupBetFn,
): BetTableRow[] => {
  return bets.flatMap<BetTableRow>((bet) =>
    Array.isArray(bet.bets) &&
    ((bet.bet_type !== "5" && bet.bet_type !== "4") ||
      (isGroupBetCondition(bet) && bet.bet_type !== "4"))
      ? bet.bets.map<BetTableRow>((subBet, index) => {
          const safeWager = toSafeNumber(
            subBet?.wager_amount ?? bet?.wager_amount,
          );
          const safePayout =
            subBet?.payout !== undefined && subBet?.payout !== null
              ? toSafeNumber(subBet.payout)
              : toSafeNumber(bet?.payout);
          return {
            ...subBet,
            balance: index === 0 ? bet.balance : "-",
            bet_id: `${bet.bet_id}-${index + 1}`,
            money_type: bet.money_type,
            odds_provider: (bet as BetApiRecord & { odds_provider?: unknown }).odds_provider,
            display_status: handleBetStatus(subBet),
            prematch_live: bet.prematch_live,
            bet_type: bet.bet_type,
            odds: subBet?.selection?.market?.outcome?.find(
              (outcome) => outcome?.chosen === 1 || outcome?.chosen === "1",
            )?.odds,
            created_at: bet.created_at,
            is_group_bet: true,
            original_bet_id: bet.bet_id,
            title: subBet?.title ?? "-",
            group_bet_index: index,
            finalized: subBet.finalized ?? bet.finalized,
            won: subBet.won ?? bet?.won,
            canceled: subBet.cancelled ?? bet?.cancelled,
            void_factor: subBet.void_factor ?? bet?.void_factor,
            wager_amount: safeWager,
            payout: safePayout,
            market: handleMarket(subBet),
            selection: handleSelection(subBet),
            is_multibet: true,
          };
        })
      : [
          {
            ...bet,
            title: bet?.title ?? "-",
            market: handleMarket(bet),
            selection: handleSelection(bet),
            wager_amount: toSafeNumber(bet?.wager_amount),
            payout: bet?.payout != null ? toSafeNumber(bet.payout) : 0,
          },
        ],
  );
};

export const processParlayBets = (bets: BetTableRow[]): BetTableRow[] => {
  return bets.map<BetTableRow>((bet) => {
    const isParlay = isTruthyFlag(bet?.is_parlay);
    if (isParlay || bet.bet_type === "4") {
      const betChildren: BetApiRecord[] | null = bet?.bets ?? null;

      const buildChild = (
        child: BetApiRecord,
        index: number,
      ): BetTableRow => ({
        bet_id: "",
        custom_bet_id: `${bet.bet_id}-${index + 1}`,
        created_at: "",
        money_type: "",
        pre_match_live: "",
        wager_amount: "",
        payout: "",
        display_status: handleBetStatus(
          child,
          bet?.bet_type === "4" ? "Pending" : "-",
        ),
        bet_type: bet?.bet_type,
        event_id: child?.event_id ?? "",
        odds:
          child?.selection?.market?.outcome?.find(
            (o) => o?.chosen === 1 || o?.chosen === "1",
          )?.odds ?? child?.selection?.market?.outcome?.[0]?.odds ?? "-",
        edit: "",
        potential_payout: bet?.bet_type === "4" ? "-" : "",
        balance: "",
        title: child?.title ?? "-",
        market: handleMarket(child),
        selection:
          child?.selection?.market?.outcome?.find(
            (o) => o?.chosen === 1 || o?.chosen === "1",
          )?.name ??
          child?.selection?.market?.outcome?.[0]?.name ??
          "-",
        isChild: true,
      });

      const children = betChildren?.map(buildChild);

      const parentSelection: BetSelection | undefined = bet?.selection && typeof bet.selection === "object"
        ? (bet.selection as BetSelection)
        : undefined;

      return {
        ...bet,
        display_status: bet?.bet_type === "4" ? "-" : bet?.display_status,
        custom_bet_id: bet?.bet_id,
        event_id: "-",
        market: "-",
        selection: "-",
        selectionArray: parentSelection,
        title: "-",
        children,
        isChild: false,
        odds: bet?.bet_type === "4" ? "-" : formatCombinedOdds(bet?.combined_odds),
      };
    }
    return bet;
  });
};

export const isGroupBet = (bet: BetApiRecord | undefined): boolean => {
  return (
    bet?.bet_type === "5" &&
    (bet?.combined_odds === 0 ||
      bet?.combined_odds === "0.00" ||
      bet?.combined_odds === "0")
  );
};
